import { type ApolloQueryResult } from '@apollo/react-hooks';
import { ExpandLess, HelpOutline, Lock, LockOpen } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Modal,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { array, number, object, string } from 'yup';
import {
  type AlertDetails,
  Button,
  ButtonContainer,
  DropdownSelect,
  type DropDownSelectOption,
  IconButton,
  SearchableSelect,
  Select,
  type SelectOption,
  TextField,
} from '../../../../../../../components';
import { FundingSource } from '../../../../../../Nomination/components/NominationForm/FundingModule/types';
import {
  useLockPanelFilter,
  useUnlockPanelFilter,
} from '../../../../../graphql';
import { COMMON_FILTER_TEXT, PAGE_TEXT } from '../../../../../lang';
import { defaultAppliedFilter } from '../../GSPanelDetails';
import type { PanelAppliedFilter, PanelLockedFilter } from './types';
import { getProcessedJsonFilter, isEmptyTableFilter } from './utils';

type PanelTagFilterProps = {
  dropdownTagOptions: DropDownSelectOption[];
  appliedFilter: PanelAppliedFilter;
  panelResultOptions: SelectOption[];
  dualPartnerUniversityList: SelectOption[];
  showDeptPriorityNo: boolean;
  showPartnerUniversity: boolean;
  fundingSource: string;
  panelId: string;
  lockOn: boolean;
  enableSendLockedFilter: boolean;
  lockedFilter?: PanelLockedFilter;
  refetch: () => Promise<ApolloQueryResult<any>>;
  handleApplyTagFilter: (value: PanelAppliedFilter) => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
  handleManageAlert: (alertDetail: AlertDetails) => void;
  handleSetLock: (lock: boolean) => void;
};

const advancedSearchExpr: Array<{ abbr: string; name: string }> = [
  { abbr: 'dept', name: 'Department Code' },
  { abbr: 'aiae', name: 'Academic Excellence Score (AI System)' },
  { abbr: 'airp', name: 'Research Ability and Potential Score (AI System)' },
  { abbr: 'ails', name: 'Leadership Ability and Societal Score (AI System)' },
  {
    abbr: 'aici',
    name: 'Communication and Interpersonal Skills Score (AI System)',
  },
  { abbr: 'aire', name: 'Resilience Capability Score (AI System)' },
  { abbr: 'aios', name: 'Organisational Skills Score (AI System)' },
  { abbr: 'aicd', name: 'Cultural Diversity Score (AI System)' },
  { abbr: 'aius', name: 'University Support Score (AI System)' },
  { abbr: 'aist', name: 'Strengths Score (AI System)' },
  { abbr: 'aist_scale', name: 'Strengths Scale (AI System)' },
  { abbr: 'aidq', name: 'Distinctive Quality Score (AI System)' },
  { abbr: 'aidq_scale', name: 'Distinctive Quality Scale (AI System)' },
  { abbr: 'ae', name: 'Academic Excellence Score (Departmental Override)' },
  {
    abbr: 'rp',
    name: 'Research Ability and Potential Score (Departmental Override)',
  },
  {
    abbr: 'ls',
    name: 'Leadership Ability and Societal Score (Departmental Override)',
  },
  {
    abbr: 'ci',
    name: 'Communication and Interpersonal Skills Score (Departmental Override)',
  },
  { abbr: 're', name: 'Resilience Capability Score (Departmental Override)' },
  { abbr: 'os', name: 'Organisational Skills Score (Departmental Override)' },
  { abbr: 'cd', name: 'Cultural Diversity Score (Departmental Override)' },
  { abbr: 'us', name: 'University Support Score (Departmental Override)' },
  { abbr: 'st', name: 'Strengths Score (Departmental Override)' },
  { abbr: 'st_scale', name: 'Strengths Scale (Departmental Override)' },
  { abbr: 'dq', name: 'Distinctive Quality Score (Departmental Override)' },
  {
    abbr: 'dq_scale',
    name: 'Distinctive Quality Scale (Departmental Override)',
  },
  { abbr: 'score', name: 'Department Score for Relevant Funding Schema' },
  { abbr: 'aiscore', name: 'System Score for Relevant Funding Schema' },
  { abbr: 'fdgpa', name: 'First Degree GPA (Based on 4.0)' },
  {
    abbr: 'fdrank',
    name: 'First Degree University Ranking (Mixed QS, THE, ARWU)',
  },
  { abbr: 'sdgpa', name: 'Second Degree GPA (Based on 4.0)' },
  {
    abbr: 'sdrank',
    name: 'Second Degree University Ranking (Mixed QS, THE, ARWU)',
  },
  { abbr: 'ishkpfs', name: 'HKPFS Nominee' },
  { abbr: 'remainsy', name: 'Supervisor Remaining Studentship Years' },
  { abbr: 'deptPriority', name: 'Departmental order' },
  {
    abbr: 'partnerPriority',
    name: 'Sort nominations by department in descending order within each partner university',
  },
];

const getEnvExprExampleMap: Partial<Record<FundingSource, string[]>> = {
  [FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME]: JSON.parse(
    process.env.REACT_APP_HKFPS_PANEL_EXPR_EG ?? '[]',
  ),
  [FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME]: JSON.parse(
    process.env.REACT_APP_PPPFS_PANEL_EXPR_EG ?? '[]',
  ),
  [FundingSource.INTERNATIONAL_PHD_FELLOWSHIP_SCHEME]: JSON.parse(
    process.env.REACT_APP_IPFS_PANEL_EXPR_EG ?? '[]',
  ),
  [FundingSource.POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME]: JSON.parse(
    process.env.REACT_APP_COMPETITIVE_PANEL_EXPR_EG ?? '[]',
  ),
  [FundingSource.DUAL_PHD_DEGREE_PROGRAMME]: JSON.parse(
    process.env.REACT_APP_DUAL_PANEL_EXPR_EG ?? '[]',
  ),
};

const PanelTagFilter: React.FunctionComponent<PanelTagFilterProps> = (
  props: PanelTagFilterProps,
) => {
  const {
    dropdownTagOptions,
    dualPartnerUniversityList,
    appliedFilter,
    enableSendLockedFilter,
    panelResultOptions,
    showDeptPriorityNo,
    showPartnerUniversity,
    panelId,
    fundingSource: funding,
    lockOn,
    lockedFilter,
    refetch,
    handleApplyTagFilter,
    handleLoading,
    handleManageAlert,
    handleSetLock,
  } = props;

  const [openExprModal, setOpenExprModal] = useState<boolean>(false);
  const [expandFilter, setExpandFilter] = useState<boolean>(true);
  const dropdownPanelResultOptions: DropDownSelectOption[] =
    panelResultOptions.map(item => ({
      value: item.value?.toString() ?? '',
      label: item.label?.toString() ?? '',
    }));

  // Extract the abbreviations into a set for quick lookup
  const validAbbrSet = new Set(advancedSearchExpr.map(item => item.abbr));

  const formik = useFormik<PanelAppliedFilter>({
    initialValues: appliedFilter,
    validationSchema: object({
      tags: array().of(string()),
      panelResults: array().of(string()),
      priorityOrderRange: object({
        min: number()
          .min(1)
          .test('min-max', 'Min cannot be greater than max', function (min) {
            const { max } = this.parent;
            if (min === undefined || max === undefined) return true;
            return min <= max;
          }),
        max: number().min(1),
      }).optional(),
      serialNo: number().min(1).optional(),
      expr: string()
        .optional()
        .nullable()
        .test('is-valid-expr', 'Invalid expression', function (value) {
          if (!value) return true; // If expr is optional and not provided, it's valid

          // Extract all sequences that start with a letter and may include numbers
          // exclude those sequences if they are enclosed in single or double quotes

          const matches = value.match(
            /(?<!['"])\b[a-zA-Z][a-zA-Z0-9_]*\b(?!['"])/g,
          );

          // Check if all extracted sequences are valid abbreviations
          if (matches) {
            const invalidAbbr = matches.find(abbr => !validAbbrSet.has(abbr));
            if (invalidAbbr) {
              return this.createError({
                message: `Invalid '${invalidAbbr}'`,
                path: this.path,
              });
            }
            return true;
          }

          // If no valid abbreviations are found, the expression is invalid
          return this.createError({
            message: 'Invalid expression',
            path: this.path,
          });
        }),

      partnerUniversity: string().optional().nullable(),
    }),

    onSubmit: handleApplyTagFilter,
  });

  const handleFilterChange = async (
    fieldName: string,
    selectedValue: string[],
  ): Promise<void> => {
    await formik.setFieldValue(fieldName, selectedValue);
  };

  const handleOpenFilter = (): void => {
    setExpandFilter(!expandFilter);
  };
  const handleClearTagFilter = (): void => {
    handleApplyTagFilter(defaultAppliedFilter);
    formik.resetForm();
  };

  useEffect(() => {
    // once appliedFilter changes
    // reset the form

    formik.resetForm({
      values: appliedFilter,
    });
  }, [appliedFilter]);

  const lockPanelFilter = useLockPanelFilter();
  const unlockPanelFilter = useUnlockPanelFilter();

  const handleLockPanelFilter = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await lockPanelFilter({
          variables: {
            panelId,
            filter: getProcessedJsonFilter(formik.values),
          },
        });
        handleManageAlert({
          alertContent: `Locked and Applied the Filter Successfully`,
          alertType: 'success',
        });
        await refetch();
        handleSetLock(true);
      } catch (error) {
        handleManageAlert({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
      }
    });
  };

  const handleUnlockPanelFilter = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await unlockPanelFilter({
          variables: {
            panelId,
          },
        });
        await refetch();
        handleSetLock(false);
        // force to re-render the whole list
      } catch (error) {
        handleManageAlert({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
      }
    });
  };
  const handleToggleLock = async (): Promise<void> => {
    if (lockOn) {
      // 1. cancel the lock,
      // 2. clear the saved filter in DB (or not?)
      await handleUnlockPanelFilter();
    } else {
      // 1. trigger the handleApplyTagFilter function
      // 2. call panelFilterLock end point to pass the filter
      // 3. only disable related filter
      await handleLockPanelFilter();
    }
  };

  const exprExamples: SelectOption[] = (
    getEnvExprExampleMap[funding as FundingSource] ?? []
  ).map(item => ({
    value: item,
    label: item,
  }));
  const halfLength = Math.floor(advancedSearchExpr.length / 2) + 1;
  const leftExpr = advancedSearchExpr.slice(0, halfLength);
  const rightExpr = advancedSearchExpr.slice(halfLength);
  const isEmptyFilter = isEmptyTableFilter(formik.values);
  return (
    <>
      <Accordion className="mb-4 min-w-[80rem]" expanded={expandFilter}>
        <AccordionSummary
          onClick={handleOpenFilter}
          style={{
            background: '#F1F1F1',
            color: '#9B9B9B',
            minHeight: 'auto',
          }}
          sx={{
            '& .css-o4b71y-MuiAccordionSummary-content.Mui-expanded': {
              margin: '0 !important',
            },
            '.MuiAccordionSummary-content': {
              margin: '0 0 0 4px !important',
              alignItems: 'center',
            },
          }}
        >
          <div className="flex items-start">
            <ExpandLess
              style={{
                transform: expandFilter ? 'rotate(180deg)' : 'rotate(90deg)',
                color: 'grey',
                marginRight: 10,
              }}
            />
            <strong>{COMMON_FILTER_TEXT.TITLE}</strong>
          </div>
        </AccordionSummary>
        <AccordionDetails className="bg-[#F1F1F1]">
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className="w-full flex flex-col gap-2">
              <div className="w-full flex justify-center text-sm items-center gap-4">
                <Typography variant="body2" className="text-ellipsis">
                  {COMMON_FILTER_TEXT.FILTER_CATEGORY.TAGS}
                </Typography>
                <div className="w-[240px]">
                  <DropdownSelect
                    fullWidth
                    options={dropdownTagOptions}
                    value={formik.values.tags}
                    disabled={lockOn && lockedFilter?.tags !== undefined}
                    onChange={async selectedValue => {
                      await handleFilterChange('tags', selectedValue);
                    }}
                  />
                </div>

                <Typography variant="body2" className="text-ellipsis">
                  {COMMON_FILTER_TEXT.FILTER_CATEGORY.RESULTS}
                </Typography>
                <div className="w-[240px]">
                  <DropdownSelect
                    fullWidth
                    options={dropdownPanelResultOptions}
                    value={formik.values.panelResults}
                    disabled={
                      lockOn && lockedFilter?.panelResults !== undefined
                    }
                    onChange={async selectedValue => {
                      await handleFilterChange('panelResults', selectedValue);
                    }}
                  />
                </div>
                {showDeptPriorityNo && (
                  <>
                    <Typography variant="body2" className="text-ellipsis">
                      {COMMON_FILTER_TEXT.FILTER_CATEGORY.PRIORITY_ORDER}
                    </Typography>
                    <div className="w-[240px] flex flex-row items-center">
                      <TextField
                        className="w-[80px]"
                        size="small"
                        type="number"
                        inputProps={{
                          step: 1,
                          min: 1,
                          max: formik.values?.priorityOrderRange?.max ?? 999,
                        }}
                        name="priorityOrderRange.min"
                        disabled={
                          lockOn &&
                          lockedFilter?.priorityOrderRange?.min !== undefined
                        }
                        value={formik.values?.priorityOrderRange?.min ?? ''}
                        onChange={async (
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const value = e.target.value;
                          await formik.setFieldValue(
                            'priorityOrderRange.min',
                            value === '' ? undefined : Number(value),
                          );
                        }}
                        error={Boolean(formik.errors?.priorityOrderRange)}
                      />
                      <p className="px-4 text-lg">-</p>
                      <TextField
                        className="w-[80px]"
                        size="small"
                        type="number"
                        disabled={
                          lockOn &&
                          lockedFilter?.priorityOrderRange?.max !== undefined
                        }
                        name="priorityOrderRange.max"
                        inputProps={{
                          min: formik?.values.priorityOrderRange?.min ?? 1,
                          step: 1,
                        }}
                        value={formik.values?.priorityOrderRange?.max ?? ''}
                        onChange={async (
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const value = e.target.value;
                          await formik.setFieldValue(
                            'priorityOrderRange.max',
                            value === '' ? undefined : Number(value),
                          );
                        }}
                        error={Boolean(formik.errors.priorityOrderRange)}
                      />
                    </div>
                  </>
                )}

                <Typography variant="body2" className="text-ellipsis">
                  {COMMON_FILTER_TEXT.FILTER_CATEGORY.SERIAL_NO}
                </Typography>
                <div className="w-[100px]">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    name="serialNo"
                    disabled={lockOn && lockedFilter?.serialNo !== undefined}
                    inputProps={{
                      min: 1,
                      step: 1,
                    }}
                    value={formik.values.serialNo}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.serialNo)}
                  />
                </div>
              </div>

              {showPartnerUniversity && (
                <div className="w-full flex justify-center text-sm items-center gap-4">
                  <Typography variant="body2" className="text-ellipsis">
                    {COMMON_FILTER_TEXT.FILTER_CATEGORY.PARTNER_UNI}
                  </Typography>

                  <div className="w-[1000px]">
                    <Select
                      fullWidth
                      size="small"
                      name="partnerUniversity"
                      options={dualPartnerUniversityList}
                      value={formik.values.partnerUniversity}
                      disabled={
                        lockOn && lockedFilter?.partnerUniversity !== undefined
                      }
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
              )}
              <div className="w-full flex justify-center text-sm items-start gap-4">
                <Typography variant="body2" className="text-ellipsis !mt-3">
                  {COMMON_FILTER_TEXT.FILTER_CATEGORY.ADVANCED_SEARCH}
                </Typography>
                <div
                  className={showDeptPriorityNo ? 'w-[1004px]' : 'w-[650px]'}
                >
                  <SearchableSelect
                    options={exprExamples}
                    fullWidth
                    name="expr"
                    size="small"
                    disabled={lockOn && lockedFilter?.expr !== undefined}
                    value={formik.values.expr}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.expr)}
                    helperText={formik.errors.expr}
                  />
                </div>
                <IconButton
                  icon={<HelpOutline sx={{ color: '#646464' }} />}
                  onClick={() => {
                    setOpenExprModal(true);
                  }}
                />
              </div>
            </div>
            <ButtonContainer
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              mt={2}
              spacing={1}
            >
              <Button
                variant="contained"
                type="submit"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {PAGE_TEXT.APPLY}
              </Button>
              <Button
                variant="outlined"
                size="small"
                className="!bg-white"
                disabled={lockOn}
                onClick={handleClearTagFilter}
                sx={{ textTransform: 'none' }}
              >
                {PAGE_TEXT.CLEAR}
              </Button>

              <div>
                <IconButton
                  disabled={!enableSendLockedFilter || isEmptyFilter}
                  color={enableSendLockedFilter ? 'primary' : 'default'}
                  icon={lockOn ? <Lock /> : <LockOpen />}
                  onClick={handleToggleLock}
                />
              </div>
            </ButtonContainer>
          </form>
        </AccordionDetails>
      </Accordion>

      <Modal
        open={openExprModal}
        onClose={() => {
          setOpenExprModal(false);
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1200px',
            bgcolor: 'background.paper',
            backgroundColor: '#E6E6E6',
            boxShadow: 24,
            borderRadius: '10px',
            padding: '16px',
            fontSize: '12px',
            lineHeight: '22px',
          }}
        >
          <div
            className="pb-2"
            dangerouslySetInnerHTML={{
              __html:
                COMMON_FILTER_TEXT.FILTER_CATEGORY.ADVANCED_SEARCH_TOOLTIP,
            }}
          />
          <div className="bg-white rounded-lg p-4">
            <div className="grid grid-cols-2 divide-x-2 divide-gray-400">
              <div>
                {leftExpr.map((item, index) => (
                  <div key={index}>
                    <b className="text-[#951D1D]">{item.abbr}</b>:&nbsp;
                    {item.name}
                  </div>
                ))}
              </div>
              <div className="pl-16">
                {rightExpr.map((item, index) => (
                  <div key={index}>
                    <b className="text-[#951D1D]">{item.abbr}</b>:&nbsp;
                    {item.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};

const MemoizedPanelTagFilter = React.memo(PanelTagFilter);
export { MemoizedPanelTagFilter };
